import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { showSuccess, showError } from "../../utils/dialog";
import talentLogo from "../../assets/talentlogo2.png";
import { BASE_URL } from "../../config";
import DateInput from "../../components/DateInput";
import {
  certDisplayName,
  getRegistrationProvince,
  hidesAlcoholServerCert,
  isSmartServe,
} from "../../utils/registrationProvince";
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from "../../utils/staffDocumentValidation";

// Certificates whose expiry date must be captured with the file
const requiresCertExpiry = (name) => /smart\s*serve|food\s*handler/i.test(String(name || ""));

const Certifications = ({ itemId = "certifications" }) => {
  const navigate = useNavigate();
  const [accountId, setAccountId] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState({});
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [activeCertId, setActiveCertId] = useState(null);
  const [uploadedCertifications, setUploadedCertifications] = useState([]);
  const [showUploadedFiles, setShowUploadedFiles] = useState(false);
  // Smart Serve hidden because the profile province has no alcohol server program (territories)
  const [hiddenProvinceCerts, setHiddenProvinceCerts] = useState([]);
  // Profile province: Smart Serve is shown under that province's program name
  const [province, setProvince] = useState("");
  // Expiry dates (YYYY-MM-DD) keyed by certification_id, sent with the file upload
  const [certExpiryDates, setCertExpiryDates] = useState({});
  // Pending modal input for certificates that need an expiry date
  const [modalFile, setModalFile] = useState(null);
  const [modalExpiry, setModalExpiry] = useState("");
  const [modalErrors, setModalErrors] = useState({});

  useEffect(() => {
    const loadData = async () => {
      const id = localStorage.getItem("accountId");
      setAccountId(id);

      if (!id) return;

      // Get the required certifications from localStorage (set by WorkPref)
      const certData = localStorage.getItem(`certs_${id}_work-preferences`);
      if (certData) {
        const allCerts = JSON.parse(certData);
        const profileProvince = getRegistrationProvince(id);
        setProvince(profileProvince);
        const hideSmartServe = hidesAlcoholServerCert(profileProvince);
        const parsedCerts = hideSmartServe ? allCerts.filter((c) => !isSmartServe(c.certification_name)) : allCerts;
        setHiddenProvinceCerts(
          hideSmartServe ? allCerts.filter((c) => isSmartServe(c.certification_name)).map((c) => c.certification_name) : []
        );
        setCertifications(parsedCerts);

        // Initialize selectedCertifications with empty file references
        const initial = {};
        parsedCerts.forEach(cert => {
          initial[cert.certification_id] = null;
        });
        setSelectedCertifications(initial);
      }

      // Load existing uploaded certifications from server
      // Wait a bit for accountId to be set, then load
      setTimeout(async () => {
        try {
          const certsResponse = await axios.get(
            `${BASE_URL}/api/personnel/accounts/${id}/certifications`,
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
          );
          
          // Get all certifications (both with and without files)
          const allCerts = certsResponse.data.data || [];
          console.log('[CERT LOAD] Received certifications from API:', allCerts.map(c => ({
            cert_id: c.certification_id,
            file_id: c.file_id,
            file_id_type: typeof c.file_id,
            file_name: c.file_name,
            file_guid: c.file_guid
          })));
          
          // Filter to only show certifications that have files uploaded (for the uploaded section)
          const uploadedCerts = allCerts.filter(cert => cert.file_id);
          
          // Set all certifications (including those without files) so we can track which ones are linked
          setUploadedCertifications(allCerts);
          
          // Show uploaded files section if there are any with files
          if (uploadedCerts.length > 0) {
            setShowUploadedFiles(true);
          }

          // Populate selectedCertifications with uploaded file info so they show in form fields
          // This includes both certifications with files and those without (to show they're linked)
          setSelectedCertifications(prev => {
            const updated = { ...prev };
            allCerts.forEach(cert => {
              // Only create placeholders for certifications that have files uploaded
              if (cert.file_id) {
                // Use file_name from the backend (original filename)
                const fileName = cert.file_name || "Uploaded file";
                
                // Create a placeholder object to represent the uploaded file
                // This allows the form to display the uploaded file name
                // Always set it if it's null or if it's already an uploaded placeholder (to refresh the data)
                if (!updated[cert.certification_id] || 
                    (updated[cert.certification_id] && updated[cert.certification_id].isUploaded)) {
                  updated[cert.certification_id] = {
                    name: fileName,
                    isUploaded: true,
                    employeeCertId: cert.employee_certification_id,
                    file_id: cert.file_id
                  };
                }
              } else {
                // For certifications without files, mark them as linked but not uploaded
                // This way they show in the form but indicate they need a file
                if (!updated[cert.certification_id] || updated[cert.certification_id] === null) {
                  updated[cert.certification_id] = {
                    name: "No file uploaded",
                    isUploaded: false,
                    isLinked: true,
                    employeeCertId: cert.employee_certification_id,
                    needsFile: true
                  };
                }
              }
            });
            return updated;
          });
        } catch (fetchErr) {
          console.warn("[Certifications] Failed to fetch uploaded certifications:", fetchErr);
        }
      }, 100);
    };

    loadData();
  }, []);

  const handleFileSelect = (certificationId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validationMessage = validateStaffDocumentFile(file);
    if (validationMessage) {
      showError(validationMessage);
      event.target.value = "";
      return;
    }

    console.log('[CERT FILE SELECT] File selected:', { certId: certificationId, fileName: file.name, fileSize: file.size });

    setSelectedCertifications(prev => ({
      ...prev,
      [certificationId]: file // Store the actual File object
    }));

    // Close modal after selection
    setShowUploadModal(false);
    setActiveCertId(null);
  };

  const getCertName = (certId) =>
    certifications.find((c) => String(c.certification_id) === String(certId))?.certification_name;

  const openUploadModal = (certId) => {
    const saved = uploadedCertifications.find((uc) => String(uc.certification_id) === String(certId))?.expiry_date;
    setModalFile(null);
    setModalExpiry(certExpiryDates[certId] || (saved ? String(saved).slice(0, 10) : ""));
    setModalErrors({});
    setActiveCertId(certId);
    setShowUploadModal(true);
  };

  // Save for Smart Serve / Food Handlers: both the file and the expiry date are required
  const saveModalWithExpiry = () => {
    const errors = {};
    if (!modalFile) errors.file = "Please attach a file.";
    if (!modalExpiry) errors.expiry = "Expiry Date is required.";
    setModalErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSelectedCertifications((prev) => ({ ...prev, [activeCertId]: modalFile }));
    setCertExpiryDates((prev) => ({ ...prev, [activeCertId]: modalExpiry }));
    closeUploadModal();
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setActiveCertId(null);
  };

  const handleRemove = (certId) => {
    setSelectedCertifications(prev => ({
      ...prev,
      [certId]: null
    }));
  };

  const handleSubmit = async () => {
    if (!accountId) {
      showError("Account ID not found");
      return;
    }

    console.log('[CERT SUBMIT] Starting submission...');
    console.log('[CERT SUBMIT] Current selectedCertifications:', Object.keys(selectedCertifications).map(key => ({
      certId: key,
      value: selectedCertifications[key],
      isFile: selectedCertifications[key] instanceof File,
      type: typeof selectedCertifications[key]
    })));

    try {
      setLoading(true);

      // Filter for only those that have a file selected (real File objects, not placeholders)
      const entries = Object.entries(selectedCertifications).filter(([_, file]) => {
        // Only include real File objects, not placeholder objects
        return file !== null && file instanceof File;
      });

      console.log('[CERT SUBMIT] Filtered File entries:', entries.length);
      entries.forEach(([certId, file]) => {
        console.log(`[CERT SUBMIT] - Cert ${certId}: ${file.name} (${file.size} bytes)`);
      });

      if (entries.length === 0) {
        console.warn('[CERT SUBMIT] ⚠️ No files selected for upload');
        showError("Please select files by clicking 'Edit' next to each certification and choosing a file before submitting");
        setLoading(false);
        return;
      }

      const missingExpiry = entries
        .filter(([certId]) => requiresCertExpiry(getCertName(Number(certId))) && !certExpiryDates[certId])
        .map(([certId]) => certDisplayName(getCertName(Number(certId)), province));
      if (missingExpiry.length > 0) {
        showError(`Please add an expiry date for: ${missingExpiry.join(", ")}`);
        setLoading(false);
        return;
      }

      console.log(`[CERT SUBMIT] Will upload ${entries.length} file(s)`);

      // Upload each certification file
      for (const [certId, file] of entries) {
        // Skip if it's a placeholder object (already uploaded, not a real file)
        if (file && file.isUploaded && !(file instanceof File)) {
          continue; // Skip placeholder objects, only upload real File objects
        }

        // Check if this certification already exists (even without file)
        const existingCert = uploadedCertifications.find(
          uc => uc.certification_id === parseInt(certId)
        );

        // Also check if there's an uploaded file info in selectedCertifications
        const selectedCert = selectedCertifications[certId];
        const hasUploadedInfo = selectedCert && selectedCert.isUploaded && selectedCert.employeeCertId;

        let employeeCertId;

        if (existingCert && existingCert.employee_certification_id) {
          // Use existing certification (even if it doesn't have a file yet)
          employeeCertId = existingCert.employee_certification_id;
          console.log('[CERT UPLOAD] Using existing employee_certification_id:', employeeCertId);
        } else if (hasUploadedInfo) {
          // Use the employee_certification_id from the placeholder
          employeeCertId = selectedCert.employeeCertId;
          console.log('[CERT UPLOAD] Using employeeCertId from placeholder:', employeeCertId);
        } else {
          // Create new certification link
          console.log('[CERT UPLOAD] Creating new certification link for certId:', certId);
          const certRes = await axios.post(
            `${BASE_URL}/api/personnel/accounts/${accountId}/certifications`,
            { certification_id: certId },
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
          );
          employeeCertId = certRes.data.insertId;
          console.log('[CERT UPLOAD] Created new employee_certification_id:', employeeCertId);
        }

        // Upload/Update the file (only if it's a real File object)
        if (employeeCertId && file instanceof File) {
          console.log('[CERT UPLOAD] Uploading file for certification:', {
            certId,
            employeeCertId,
            fileName: file.name,
            fileSize: file.size
          });
          
          const formData = new FormData();
          formData.append("file", file);
          if (certExpiryDates[certId]) {
            formData.append("expiry_date", certExpiryDates[certId]);
          }

          try {
            const uploadResponse = await axios.put(
              `${BASE_URL}/api/personnel/accounts/${accountId}/certifications/${employeeCertId}`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${localStorage.getItem("token")}`
                }
              }
            );
            console.log('[CERT UPLOAD] ✅ Upload successful:', {
              certId,
              employeeCertId,
              fileName: file.name,
              response: uploadResponse.data,
              status: uploadResponse.status,
              url: `${BASE_URL}/api/personnel/accounts/${accountId}/certifications/${employeeCertId}`
            });
          } catch (uploadErr) {
            console.error('[CERT UPLOAD] ❌ Upload failed:', {
              certId,
              employeeCertId,
              fileName: file.name,
              error: uploadErr.response?.data || uploadErr.message,
              status: uploadErr.response?.status,
              url: `${BASE_URL}/api/personnel/accounts/${accountId}/certifications/${employeeCertId}`
            });
            throw uploadErr; // Re-throw to be caught by outer try-catch
          }
        } else {
          console.warn('[CERT UPLOAD] Skipping upload:', {
            hasEmployeeCertId: !!employeeCertId,
            isFile: file instanceof File,
            fileType: typeof file
          });
        }
      }

      // Refresh uploaded certifications list to show newly uploaded files
      await refreshUploadedCertifications();

      // Keep the selected files in the form fields (don't clear them)
      // This allows users to see what was uploaded

      showSuccess(`Certifications submitted successfully`);

      // Update status to complete
      localStorage.setItem(`status_${accountId}_${itemId}`, "complete");

      // Sync status with server (non-blocking - don't fail if this errors)
      try {
        await axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
          status: "complete",
          formData: {}
        });
        console.log("[Certifications] Status synced successfully with server");
      } catch (statusErr) {
        // Log but don't show error - uploads succeeded, status sync is secondary
        console.warn("[Certifications] Failed to sync status with server (non-fatal):", statusErr.response?.data || statusErr.message);
        // Status is already saved in localStorage, so user can continue
      }

      // Don't auto-navigate - let user see the uploaded files
      // User can navigate back manually using the Back button
    } catch (err) {
      console.error("Failed to upload certifications:", err);
      showError(err.response?.data?.message || err.message || "Failed to upload certifications");
    } finally {
      setLoading(false);
    }
  };

  const refreshUploadedCertifications = async () => {
    if (!accountId) return;
    
    try {
      const certsResponse = await axios.get(
        `${BASE_URL}/api/personnel/accounts/${accountId}/certifications`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      
      // Filter to only show certifications that have files uploaded
      const uploadedCerts = (certsResponse.data.data || []).filter(cert => cert.file_id);
      setUploadedCertifications(uploadedCerts);
      
      if (uploadedCerts.length > 0) {
        setShowUploadedFiles(true);

        // Also update selectedCertifications to show uploaded files in form fields
        setSelectedCertifications(prev => {
          const updated = { ...prev };
          uploadedCerts.forEach(uploadedCert => {
            // Extract filename from file_id
            let fileName = "Uploaded file";
            if (uploadedCert.file_id) {
              if (typeof uploadedCert.file_id === 'string' && uploadedCert.file_id.includes('/')) {
                fileName = uploadedCert.file_id.split('/').pop() || "Uploaded file";
              } else if (typeof uploadedCert.file_id === 'string') {
                fileName = uploadedCert.file_id;
              }
            }
            
            // Create/update placeholder object to represent the uploaded file
            // Only update if it's null or already an uploaded placeholder (preserve newly selected files)
            if (!updated[uploadedCert.certification_id] || 
                (updated[uploadedCert.certification_id] && updated[uploadedCert.certification_id].isUploaded)) {
              updated[uploadedCert.certification_id] = {
                name: fileName,
                isUploaded: true,
                employeeCertId: uploadedCert.employee_certification_id,
                file_id: uploadedCert.file_id
              };
            }
          });
          return updated;
        });
      } else {
        setShowUploadedFiles(false);
      }
    } catch (fetchErr) {
      console.warn("[Certifications] Failed to fetch uploaded certifications:", fetchErr);
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  if (certifications.length === 0) {
    return (
      <div className="accessibility-form-page">
        <div className="form-container">
          <div className="form-card">
            <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
              <h2 className="form-title" style={{ textAlign: "center" }}>Certifications</h2>
            </header>

            <section className="form-card-content">
              <p className="form-intro text-gray-600">
                No certifications required for your selected divisions and skills.
              </p>
              {hiddenProvinceCerts.length > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {hiddenProvinceCerts.join(", ")} has no equivalent program for the province on your profile, so it does not apply.
                </p>
              )}
            </section>

            <div className="form-buttons">
              <button type="button" onClick={handleBack}>Back to Registration</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if there are any files selected for upload/update
  const hasSelectedFiles = Object.values(selectedCertifications).some(f => f !== null);
  // For display purposes, check if all required certifications have files (either uploaded or newly selected)
  const allFilesSelected = certifications.every(cert => {
    const isUploaded = uploadedCertifications.some(
      uc => uc.certification_id === cert.certification_id && uc.file_id
    );
    const isSelected = selectedCertifications[cert.certification_id] !== null;
    return isUploaded || isSelected;
  });

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        <div className="form-card">
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
            <h2 className="form-title" style={{ textAlign: "center" }}>Upload Certifications</h2>
          </header>

          <section className="form-card-content">
            {/* Show uploaded certifications at the top if any exist (only those with files) */}
            {showUploadedFiles && uploadedCertifications.filter(cert => cert.file_id).length > 0 && (
              <div style={{ marginBottom: "30px", padding: "20px", backgroundColor: "#f0f9ff", border: "2px solid #3b82f6", borderRadius: "8px" }}>
                <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1e40af", marginBottom: "15px" }}>
                  ✓ Uploaded Certifications
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {uploadedCertifications.filter(cert => cert.file_id).map((cert) => {
                    // Extract filename from file_id if it's an S3 path
                    let fileName = "File uploaded";
                    if (cert.file_id) {
                      if (typeof cert.file_id === 'string' && cert.file_id.includes('/')) {
                        fileName = cert.file_id.split('/').pop() || "File uploaded";
                      } else if (typeof cert.file_id === 'string') {
                        fileName = cert.file_id;
                      }
                    }
                    
                    return (
                      <div
                        key={cert.employee_certification_id}
                        style={{
                          padding: "12px",
                          backgroundColor: "#ffffff",
                          border: "1px solid #93c5fd",
                          borderRadius: "6px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "600", color: "#1e40af", marginBottom: "4px" }}>
                            {certDisplayName(cert.certification_name, province)}
                          </div>
                          {cert.file_id && (
                            <div style={{ fontSize: "13px", color: "#059669" }}>
                              ✓ {fileName}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {hiddenProvinceCerts.length > 0 && (
              <p className="text-sm text-gray-600 mb-3">
                {hiddenProvinceCerts.join(", ")} has no equivalent program for the province on your profile, so it is not required here.
              </p>
            )}
            <p className="form-intro">
              {showUploadedFiles && uploadedCertifications.length > 0 
                ? "You can upload additional certifications or update existing ones below:"
                : "Please upload the following required certifications:"}
            </p>

            <div style={{ marginTop: "20px" }}>
              {certifications.map(cert => {
                // Check if this certification is already linked to the account
                const isLinked = uploadedCertifications.some(
                  uc => uc.certification_id === cert.certification_id
                );
                // Check if this certification has a file uploaded
                const isAlreadyUploaded = uploadedCertifications.some(
                  uc => uc.certification_id === cert.certification_id && uc.file_id
                );
                const uploadedCert = uploadedCertifications.find(
                  uc => uc.certification_id === cert.certification_id
                );

                return (
                  <div
                    key={cert.certification_id}
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      border: isAlreadyUploaded ? "2px solid #10b981" : "1px solid #e0e0e0",
                      borderRadius: "4px",
                      backgroundColor: isAlreadyUploaded ? "#f0fdf4" : "#f9f9f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}
                  >
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {certDisplayName(cert.certification_name, province)}
                        <span className="text-red-500"> *</span>
                        {isAlreadyUploaded && (
                          <span className="text-green-600 text-sm ml-2">✓ Uploaded</span>
                        )}
                      </label>
                      {cert.description && <p className="text-xs text-gray-500">{cert.description}</p>}
                      {requiresCertExpiry(cert.certification_name) && (certExpiryDates[cert.certification_id] || uploadedCert?.expiry_date) && (
                        <p className="text-xs text-gray-600 mt-1">
                          Expiry Date: {String(certExpiryDates[cert.certification_id] || uploadedCert.expiry_date).slice(0, 10)}
                        </p>
                      )}
                      {isAlreadyUploaded && uploadedCert?.file_id && (
                        <p className="text-xs text-green-600 mt-1">
                          File: {typeof uploadedCert.file_id === 'string' && uploadedCert.file_id.includes('/') 
                            ? uploadedCert.file_id.split('/').pop() 
                            : 'Uploaded'}
                        </p>
                      )}
                    </div>
                    <div>
                      {(selectedCertifications[cert.certification_id] || isLinked) ? (
                        <div className="flex items-center">
                          {/* Check if it's a File object (newly selected) */}
                          {selectedCertifications[cert.certification_id] instanceof File ? (
                            <>
                              <span className="text-green-600 text-sm mr-2">
                                ✓ {selectedCertifications[cert.certification_id].name}
                              </span>
                              <span className="text-xs text-gray-500 mr-2">(Ready to upload)</span>
                            </>
                          ) : isAlreadyUploaded && uploadedCert?.file_id ? (
                            /* Already uploaded - show uploaded status */
                            <>
                              <span className="text-green-600 text-sm mr-2">
                                ✓ {typeof uploadedCert.file_id === 'string' && uploadedCert.file_id.includes('/') 
                                    ? uploadedCert.file_id.split('/').pop() 
                                    : selectedCertifications[cert.certification_id]?.name || 'Uploaded file'}
                              </span>
                              <span className="text-xs text-gray-500 mr-2">(Uploaded)</span>
                            </>
                          ) : selectedCertifications[cert.certification_id]?.needsFile ? (
                            /* Linked but needs file */
                            <span className="text-orange-600 text-sm mr-2">
                              ⚠ Click 'Edit' to upload file
                            </span>
                          ) : (
                            /* Default case */
                            <>
                              <span className="text-green-600 text-sm mr-2">
                                ✓ {selectedCertifications[cert.certification_id]?.name || "File"}
                              </span>
                              {selectedCertifications[cert.certification_id]?.isUploaded && (
                                <span className="text-xs text-gray-500 mr-2">(Uploaded)</span>
                              )}
                            </>
                          )}
                          <button type="button" className="text-blue-600 text-xs mr-2" onClick={() => openUploadModal(cert.certification_id)}>Edit</button>
                          <button type="button" className="text-red-600 text-xs" onClick={() => handleRemove(cert.certification_id)}>Remove</button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="text-white bg-red-600 px-3 py-1 rounded"
                          style={{ float: "right", minWidth: 60 }}
                          onClick={() => openUploadModal(cert.certification_id)}
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!allFilesSelected && (
              <p className="text-sm text-orange-600 mt-4">
                Please select files for all required certifications before submitting.
              </p>
            )}
          </section>

          <div className="form-buttons">
            <button type="button" onClick={handleBack} disabled={loading}>
              Back
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={(!hasSelectedFiles && !showUploadedFiles) || loading}
              style={{
                opacity: ((hasSelectedFiles || showUploadedFiles) && !loading) ? 1 : 0.5,
                cursor: ((hasSelectedFiles || showUploadedFiles) && !loading) ? "pointer" : "not-allowed"
              }}
            >
              {loading ? "Uploading..." : hasSelectedFiles ? "Submit" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-semibold mb-2">Upload Certification File</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select your certificate file (PDF, JPG, or HEIC)
            </p>
            {requiresCertExpiry(getCertName(activeCertId)) ? (
              <>
                <label className="block text-sm font-medium mb-1">File <span className="text-red-500">*</span></label>
                <input
                  type="file"
                  accept={STAFF_DOCUMENT_ACCEPT}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    const validationMessage = f && validateStaffDocumentFile(f);
                    if (validationMessage) {
                      showError(validationMessage);
                      e.target.value = "";
                      setModalFile(null);
                      return;
                    }
                    setModalFile(f);
                    if (f) setModalErrors((prev) => ({ ...prev, file: undefined }));
                  }}
                  className="block w-full text-sm border border-gray-300 rounded-md p-2 cursor-pointer"
                />
                {modalErrors.file && <p className="text-red-600 text-xs mt-1">{modalErrors.file}</p>}

                <label className="block text-sm font-medium mt-4 mb-1">Expiry Date <span className="text-red-500">*</span></label>
                <DateInput
                  value={modalExpiry}
                  onChange={(v) => {
                    setModalExpiry(v);
                    if (v) setModalErrors((prev) => ({ ...prev, expiry: undefined }));
                  }}
                  className="block w-full text-sm border border-gray-300 rounded-md p-2"
                />
                {modalErrors.expiry && <p className="text-red-600 text-xs mt-1">{modalErrors.expiry}</p>}

                <div className="flex justify-end mt-4">
                  <button type="button" className="mr-2 px-4 py-2 rounded bg-gray-300 hover:bg-gray-400" onClick={closeUploadModal}>Cancel</button>
                  <button type="button" className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700" onClick={saveModalWithExpiry}>Save</button>
                </div>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept={STAFF_DOCUMENT_ACCEPT}
                  onChange={(e) => { handleFileSelect(activeCertId, e); }}
                  className="block w-full text-sm border border-gray-300 rounded-md p-2 cursor-pointer mb-4"
                />
                <div className="flex justify-end">
                  <button type="button" className="mr-2 px-4 py-2 rounded bg-gray-300 hover:bg-gray-400" onClick={closeUploadModal}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Certifications;
